import { useEffect, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { useDialog } from "@/context/DialogProvider";
import { Portal } from "@/components/Portal";
import {
  X, Plus, Download, Upload, Trash2, Pencil, Receipt, CheckCircle2, Clock,
} from "lucide-react";

// ---------------------------------------------------------------------------
// TenantBillingModal — super-admin manual/invoiced billing tracker for one
// tenant. Record annual invoice payments, view history, download / upload the
// invoice file. Recording a *paid* invoice enables the chosen plan and sets
// the workspace access window (valid-until = period end).
// ---------------------------------------------------------------------------
function fmtSGD(n) {
  if (n == null) return "—";
  return new Intl.NumberFormat("en-SG", { style: "currency", currency: "SGD" }).format(Number(n));
}
function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-SG", { day: "numeric", month: "short", year: "numeric" }); }
  catch { return "—"; }
}
function todayISO() { return new Date().toISOString().slice(0, 10); }
function plusYearISO(startIso) {
  const d = startIso ? new Date(startIso) : new Date();
  d.setFullYear(d.getFullYear() + 1);
  return d.toISOString().slice(0, 10);
}
function plusMonthISO(startIso) {
  const d = startIso ? new Date(startIso) : new Date();
  d.setMonth(d.getMonth() + 1);
  return d.toISOString().slice(0, 10);
}
function dayAfterISO(iso) {
  const d = new Date(iso);
  d.setDate(d.getDate() + 1);
  return d.toISOString().slice(0, 10);
}
function endForInterval(startIso, interval) {
  return interval === "monthly" ? plusMonthISO(startIso) : plusYearISO(startIso);
}

const BLANK = () => {
  const start = todayISO();
  return {
    tier_id: "", amount: "", invoice_number: "", paid_on: "",
    period_start: start, period_end: plusYearISO(start), status: "pending", notes: "",
  };
};

export default function TenantBillingModal({ tenant, tiers, onClose, onChanged, autoGenerate }) {
  const { confirm, notify } = useDialog();
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null);          // null = list view, object = add/edit form
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [bstate, setBstate] = useState(null);
  const [pkg, setPkg] = useState(null);
  const [settings, setSettings] = useState(null);   // null = hidden, object = editing
  const [savingSettings, setSavingSettings] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const [p, b] = await Promise.all([
        api.get(`/admin/tenants/${tenant.id}/payments`),
        api.get(`/admin/tenants/${tenant.id}/billing-state`).catch(() => ({ data: { billing: null } })),
      ]);
      setPayments(p.data.payments || []);
      const billing = b.data.billing || null;
      setBstate(billing);
      const pr = await api.get(`/admin/tenants/${tenant.id}/package`).catch(() => ({ data: { package: null } }));
      const loadedPkg = pr.data.package || null;
      setPkg(loadedPkg);
      // One-click "Generate renewal invoice" from the Tenants renewals panel.
      if (autoGenerate) prefillGenerate(loadedPkg, billing);
    } catch (e) { console.debug("billing load failed", e); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tenant.id]);

  // For renewals, start the new window the day AFTER the current paid-through
  // date so early renewals stay contiguous (no paid days lost). Lapsed / no
  // window → start today.
  const renewalStartFor = (billing) => {
    const au = billing?.access_until;
    if (au) {
      const d = au.slice(0, 10);
      if (d >= todayISO()) return dayAfterISO(d);
    }
    return todayISO();
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      await api.put(`/admin/tenants/${tenant.id}/billing`, {
        billing_model: settings.billing_model,
        trial_days: Number(settings.trial_days),
        grace_days: Number(settings.grace_days),
        service_start: settings.service_start || undefined,
        duration_months: settings.duration_months ? Number(settings.duration_months) : undefined,
      });
      setSettings(null);
      await load();
      onChanged && onChanged();
    } catch (e) {
      await notify({ title: "Could not save billing settings", message: formatErr(e.response?.data?.detail), variant: "err" });
    } finally { setSavingSettings(false); }
  };

  const openAdd = () => {
    const b = BLANK();
    b.tier_id = tenant.tier_id || tiers[0]?.id || "";
    setForm(b); setEditingId(null); setErr("");
  };

  // Prefill the invoice form. If a package exists, use its plan + locked price
  // + itemised lines; dates are contiguous with the current paid-through date.
  const prefillGenerate = (pkgArg, billingArg) => {
    const start = renewalStartFor(billingArg);
    if (!pkgArg) {
      setForm({
        ...BLANK(), tier_id: tenant.tier_id || tiers[0]?.id || "",
        period_start: start, period_end: plusYearISO(start),
      });
      setEditingId(null); setErr("");
      return;
    }
    const interval = pkgArg.billing_interval || "yearly";
    const end = endForInterval(start, interval);
    const items = [
      { description: `${pkgArg.tier_name || "Subscription"} plan — ${interval}`, amount: Number(pkgArg.base_price || 0) },
      ...(pkgArg.addons || []).map((a) => ({
        description: a.quantity > 1 ? `${a.label} × ${a.quantity}` : a.label,
        amount: Number(a.line_total || 0),
      })),
    ];
    const computed = items.reduce((s, i) => s + i.amount, 0);
    const locked = Number(pkgArg.locked_price || 0);
    if (Math.abs(locked - computed) > 0.005) {
      items.push({ description: "Negotiated adjustment", amount: Number((locked - computed).toFixed(2)) });
    }
    setForm({
      tier_id: pkgArg.tier_id, amount: String(locked), invoice_number: "",
      paid_on: "", period_start: start, period_end: end,
      status: "pending", notes: pkgArg.notes || "", line_items: items,
    });
    setEditingId(null); setErr("");
  };

  // Primary action: prefill from the package if one exists, else a blank form.
  const openGenerate = () => prefillGenerate(pkg, bstate);
  const openEdit = (p) => {
    setForm({
      tier_id: p.tier_id, amount: String(p.amount ?? ""), invoice_number: p.invoice_number || "",
      paid_on: p.paid_on || "", period_start: p.period_start || "", period_end: p.period_end || "",
      status: p.status || "paid", notes: p.notes || "", line_items: p.line_items || null,
    });
    setEditingId(p.id); setErr("");
  };

  const set = (k) => (e) => {
    const v = e.target.value;
    setForm((f) => {
      const next = { ...f, [k]: v };
      // Convenience: bump period_end to +1yr when start changes on a new record.
      if (k === "period_start" && !editingId) next.period_end = plusYearISO(v);
      return next;
    });
  };

  // Status ⇄ paid-on: pending clears the date; paid defaults it to today (editable).
  const setStatus = (e) => {
    const v = e.target.value;
    setForm((f) => ({ ...f, status: v, paid_on: v === "paid" ? (f.paid_on || todayISO()) : "" }));
  };

  const save = async (e) => {
    e.preventDefault(); setErr("");
    if (!form.tier_id) return setErr("Select a plan.");
    if (!form.period_start || !form.period_end) return setErr("Coverage period is required.");
    if (form.period_end <= form.period_start) return setErr("Period end must be after start.");
    if (!editingId && bstate?.access_until) {
      const paidThrough = bstate.access_until.slice(0, 10);
      if (form.period_end <= paidThrough) {
        return setErr(`Coverage end must be after the current paid-through date (${fmtDate(bstate.access_until)}), otherwise it would shorten the customer's access.`);
      }
    }
    setBusy(true);
    try {
      const payload = {
        tier_id: form.tier_id,
        amount: Number(form.amount || 0),
        invoice_number: form.invoice_number,
        paid_on: form.status === "paid" ? (form.paid_on || todayISO()) : null,
        period_start: form.period_start,
        period_end: form.period_end,
        status: form.status,
        notes: form.notes,
        line_items: form.line_items && form.line_items.length ? form.line_items : undefined,
      };
      if (editingId) await api.put(`/admin/payments/${editingId}`, payload);
      else await api.post(`/admin/tenants/${tenant.id}/payments`, payload);
      setForm(null); setEditingId(null);
      await load();
      onChanged?.();
    } catch (ex) { setErr(formatErr(ex.response?.data?.detail) || "Save failed."); }
    finally { setBusy(false); }
  };

  const del = async (p) => {
    if (!await confirm({
      title: "Delete this payment record?",
      message: `Invoice ${p.invoice_number || "(no number)"} · ${fmtSGD(p.amount)}. This removes the receipt only — it won't change the tenant's current plan.`,
      danger: true, confirmLabel: "Delete record",
    })) return;
    try { await api.delete(`/admin/payments/${p.id}`); await load(); onChanged?.(); }
    catch (ex) { await notify({ title: "Delete failed", message: formatErr(ex.response?.data?.detail), variant: "err" }); }
  };

  const downloadInvoice = async (p) => {
    try {
      const res = await api.get(`/admin/payments/${p.id}/invoice`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: res.data.type || "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `invoice-${(p.invoice_number || p.id.slice(0, 8)).replace(/\//g, "-")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (ex) { await notify({ title: "Download failed", message: formatErr(ex.response?.data?.detail), variant: "err" }); }
  };

  const uploadInvoice = async (p, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/admin/payments/${p.id}/invoice-upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      await notify({ title: "Invoice uploaded", variant: "ok" });
      await load();
    } catch (ex) { await notify({ title: "Upload failed", message: formatErr(ex.response?.data?.detail), variant: "err" }); }
  };

  return (
    <Portal><div className="vb-modal-bg" onClick={onClose} data-testid="billing-modal-bg">
      <div className="vb-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 760 }} data-testid="billing-modal">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-2">
            <Receipt size={20} style={{ color: "var(--vb-text-2)" }} />
            <div>
              <h2 className="vb-heading text-xl m-0">Billing · {tenant.company_name}</h2>
              <p className="text-xs opacity-60 m-0">Manual / invoiced (annual) payments. Recording a paid invoice enables the plan.</p>
            </div>
          </div>
          <button className="vb-btn ghost" onClick={onClose} data-testid="billing-close"><X size={16} /></button>
        </div>

        {form ? (
          <form onSubmit={save} className="space-y-3" data-testid="billing-form">
            {err && <div className="vb-pill err">{err}</div>}
            <div className="text-xs opacity-70" data-testid="billing-invoice-no">
              Invoice no:{" "}
              <strong style={{ color: "var(--vb-text-2)" }}>
                {editingId ? (form.invoice_number || "—") : "Auto-generated (INV VB-####)"}
              </strong>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <FieldLabel>Plan to enable *</FieldLabel>
                <select className="vb-input" value={form.tier_id} onChange={set("tier_id")} data-testid="billing-tier">
                  <option value="">Select a plan…</option>
                  {tiers.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
              <div>
                <FieldLabel>Amount (SGD) *</FieldLabel>
                <input className="vb-input" type="number" min="0" step="0.01" value={form.amount}
                  onChange={set("amount")} placeholder="1200.00" data-testid="billing-amount" />
              </div>
              <div>
                <FieldLabel>Status *</FieldLabel>
                <select className="vb-input" value={form.status} onChange={setStatus} data-testid="billing-status">
                  <option value="pending">Pending</option>
                  <option value="paid">Paid</option>
                </select>
              </div>
              <div>
                <FieldLabel>Paid on</FieldLabel>
                <input className="vb-input" type="date" value={form.paid_on || ""} onChange={set("paid_on")}
                  disabled={form.status !== "paid"} data-testid="billing-paid-on" />
              </div>
              <div>
                <FieldLabel>Coverage start *</FieldLabel>
                <input className="vb-input" type="date" value={form.period_start} onChange={set("period_start")} data-testid="billing-period-start" />
              </div>
              <div>
                <FieldLabel>Coverage end (access valid until) *</FieldLabel>
                <input className="vb-input" type="date" value={form.period_end} onChange={set("period_end")} data-testid="billing-period-end" />
              </div>
            </div>
            <div>
              <FieldLabel>Notes</FieldLabel>
              <textarea className="vb-input" rows={2} value={form.notes} onChange={set("notes")}
                placeholder="e.g. PO #4521, paid via bank transfer" data-testid="billing-notes" />
            </div>
            {form.line_items && form.line_items.length > 0 && (
              <div className="vb-card p-3" style={{ background: "var(--vb-bg-2)" }} data-testid="billing-line-items">
                <div className="text-[10px] uppercase tracking-wider opacity-60 mb-2">Invoice line items (from package)</div>
                {form.line_items.map((li, i) => (
                  <div key={i} className="flex justify-between text-sm py-0.5">
                    <span className="opacity-80">{li.description}</span>
                    <span className="font-medium">{fmtSGD(li.amount)}</span>
                  </div>
                ))}
                <div className="flex justify-between text-sm font-semibold mt-1 pt-1 border-t" style={{ borderColor: "var(--vb-border)" }}>
                  <span>Total</span><span>{fmtSGD(form.line_items.reduce((s, i) => s + Number(i.amount || 0), 0))}</span>
                </div>
              </div>
            )}
            <div className="flex justify-end gap-2 pt-1">
              <button type="button" className="vb-btn ghost" onClick={() => { setForm(null); setEditingId(null); }} data-testid="billing-cancel">Cancel</button>
              <button type="submit" className="vb-btn" disabled={busy} data-testid="billing-save">
                {busy ? "Saving…" : editingId ? "Save changes" : "Generate invoice"}
              </button>
            </div>
          </form>
        ) : (
          <>
            {bstate && <LifecycleStrip b={bstate} />}

            {settings ? (
              <div className="vb-card p-4 mb-3 space-y-3" data-testid="billing-settings-form">
                <div className="flex items-center justify-between">
                  <strong className="vb-heading text-sm" style={{ color: "var(--vb-text-2)" }}>Billing settings</strong>
                </div>
                <div>
                  <FieldLabel>Billing model</FieldLabel>
                  <select className="vb-input" value={settings.billing_model} onChange={(e) => setSettings({ ...settings, billing_model: e.target.value })} data-testid="settings-model">
                    <option value="invoiced">Direct / Invoiced</option>
                    <option value="subscription">Subscription (Stripe)</option>
                  </select>
                </div>
                {settings.billing_model === "invoiced" && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <FieldLabel>Service start</FieldLabel>
                      <input className="vb-input" type="date" value={settings.service_start || ""} onChange={(e) => setSettings({ ...settings, service_start: e.target.value })} data-testid="settings-start" />
                    </div>
                    <div>
                      <FieldLabel>Duration (months)</FieldLabel>
                      <input className="vb-input" type="number" min={1} value={settings.duration_months || ""} onChange={(e) => setSettings({ ...settings, duration_months: e.target.value })} data-testid="settings-duration" />
                    </div>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <FieldLabel>Trial period (days)</FieldLabel>
                    <input className="vb-input" type="number" min={0} value={settings.trial_days} onChange={(e) => setSettings({ ...settings, trial_days: e.target.value })} data-testid="settings-trial" />
                  </div>
                  <div>
                    <FieldLabel>Grace period (days)</FieldLabel>
                    <input className="vb-input" type="number" min={0} value={settings.grace_days} onChange={(e) => setSettings({ ...settings, grace_days: e.target.value })} data-testid="settings-grace" />
                  </div>
                </div>
                <div className="flex justify-end gap-2">
                  <button className="vb-btn ghost" onClick={() => setSettings(null)} data-testid="settings-cancel">Cancel</button>
                  <button className="vb-btn" onClick={saveSettings} disabled={savingSettings} data-testid="settings-save">
                    {savingSettings ? "Saving…" : "Save settings"}
                  </button>
                </div>
              </div>
            ) : null}

            <div className="flex justify-between items-center mb-3">
              <button
                className="vb-btn ghost sm" data-testid="billing-settings-open"
                onClick={() => setSettings({
                  billing_model: bstate?.billing_model === "subscription" ? "subscription" : "invoiced",
                  trial_days: bstate?.trial_days ?? 0,
                  grace_days: bstate?.grace_days ?? 0,
                  service_start: (bstate?.service_start || "").slice(0, 10),
                  duration_months: "",
                })}
              >
                <Pencil size={13} /> Billing settings
              </button>
              <button className="vb-btn" onClick={openGenerate} data-testid="billing-add"
                title={pkg ? `Auto-fills from package: ${fmtSGD(pkg.locked_price)} / ${pkg.billing_interval}` : "Create a new invoice"}>
                <Plus size={14} /> Generate Invoice
              </button>
            </div>
            {loading ? (
              <div className="text-sm opacity-60 py-6 text-center">Loading payments…</div>
            ) : payments.length === 0 ? (
              <div className="vb-card p-6 text-center text-sm opacity-70" data-testid="billing-empty">
                No payments recorded yet. Record the first annual invoice above.
              </div>
            ) : (
              <div className="space-y-2" data-testid="billing-list">
                {payments.map((p) => (
                  <div key={p.id} className="vb-card p-4 flex items-center justify-between gap-3 flex-wrap" data-testid={`billing-row-${p.id}`}>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <strong>{fmtSGD(p.amount)}</strong>
                        <span className="vb-pill" style={{ background: "var(--vb-bg-2)" }}>{p.tier_name || "—"}</span>
                        {p.status === "paid"
                          ? <span className="vb-pill ok inline-flex items-center gap-1 text-[11px]"><CheckCircle2 size={11} /> Paid</span>
                          : <span className="vb-pill warn inline-flex items-center gap-1 text-[11px]"><Clock size={11} /> Pending</span>}
                      </div>
                      <div className="text-xs opacity-70 mt-1">
                        {p.invoice_number ? `${p.invoice_number} · ` : ""}
                        {fmtDate(p.period_start)} → {fmtDate(p.period_end)}
                        {p.paid_on ? ` · paid ${fmtDate(p.paid_on)}` : ""}
                      </div>
                    </div>
                    <div className="flex gap-1 items-center">
                      <button className="vb-btn ghost sm" title="Download invoice PDF" onClick={() => downloadInvoice(p)} data-testid={`billing-download-${p.id}`}>
                        <Download size={13} />
                      </button>
                      <label className="vb-btn ghost sm" title="Upload custom invoice" style={{ cursor: "pointer" }} data-testid={`billing-upload-${p.id}`}>
                        <Upload size={13} />
                        <input type="file" accept="application/pdf,image/png,image/jpeg" style={{ display: "none" }}
                          onChange={(e) => uploadInvoice(p, e.target.files?.[0])} />
                      </label>
                      <button className="vb-btn ghost sm" title="Edit" onClick={() => openEdit(p)} data-testid={`billing-edit-${p.id}`}>
                        <Pencil size={13} />
                      </button>
                      <button className="vb-btn ghost sm" title="Delete" onClick={() => del(p)} style={{ color: "var(--vb-error)" }} data-testid={`billing-delete-${p.id}`}>
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div></Portal>
  );
}

function FieldLabel({ children }) {
  return <label className="block text-[11px] font-semibold mb-1 uppercase tracking-wider opacity-70">{children}</label>;
}


const STATUS_META = {
  trialing: { label: "Trialing", bg: "rgba(120,61,25,0.10)", fg: "#783D19" },
  active: { label: "Active", bg: "rgba(22,163,74,0.12)", fg: "#15803d" },
  grace: { label: "Grace period", bg: "rgba(217,119,6,0.14)", fg: "#b45309" },
  expired: { label: "Expired", bg: "rgba(127,29,29,0.12)", fg: "#7f1d1d" },
  suspended: { label: "Suspended", bg: "rgba(127,29,29,0.12)", fg: "#7f1d1d" },
};

function LifecycleStrip({ b }) {
  const meta = STATUS_META[b.status] || { label: b.status, bg: "var(--vb-bg-2)", fg: "var(--vb-text-2)" };
  const d = (iso) => (iso ? new Date(iso).toLocaleDateString() : "—");
  return (
    <div className="vb-card p-4 mb-3" data-testid="billing-lifecycle">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <span className="text-xs font-semibold px-2.5 py-1 rounded-full" style={{ background: meta.bg, color: meta.fg }} data-testid="billing-lifecycle-status">
          {meta.label}
        </span>
        <span className="text-xs opacity-70 capitalize">
          {b.billing_model === "subscription" ? "Subscription (Stripe)" : "Direct / Invoiced"}
        </span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
        <Metric label="Trial ends" value={d(b.trial_ends_at)} />
        <Metric label="Service ends" value={d(b.service_end)} />
        <Metric label="Grace" value={`${b.grace_days || 0} day(s)`} />
        <Metric label={b.status === "grace" ? "Locks in" : "Days left"} value={b.status === "grace" ? `${b.days_until_hard_block ?? 0}d` : (b.days_until_access_end != null ? `${b.days_until_access_end}d` : "—")} />
      </div>
    </div>
  );
}

function Metric({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider opacity-60">{label}</div>
      <div className="text-sm font-semibold" style={{ color: "var(--vb-text-2)" }}>{value}</div>
    </div>
  );
}
